<template>
  <div>
    <v-form ref="bookingForm" @submit.prevent="">
      <v-row class="justify-space-between">
        <!-- select product -->
        <v-col class="col-4" :class="{ 'pb-0': editOrAddProductMode }">
          <v-card outlined :class="{ 'pb-0': editOrAddProductMode }">
            <v-card-title class="">
              {{ $t('createBooking.composeProduct') }}
            </v-card-title>
            <v-card-text class="pb-0">
              <v-row class="no-gutters">
                <!-- products dropdown -->
                <v-col class="col-7 align-self-center mt-n7">
                  <product-selector
                    :products-and-events="productsAndEvents"
                    :selected-product-or-event-instance.sync="
                      selectedProductOrEventInstance
                    "
                    limit-product-name
                    :max-product-name-length="22"
                  />
                </v-col>
                <!-- planned date -->
                <v-col class="col-5">
                  <s-date-picker
                    :date.sync="selectedDate"
                    :label="$t('booking.selectDate')"
                    :rules="[(v) => !!v || $t('v5.selectDate')]"
                    :allowed-dates="allowedDates"
                    :min="minDate"
                    :max="maxDate"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- product definition -->
        <v-col class="col-8" :class="{ 'pb-0 pl-0': editOrAddProductMode }">
          <v-card
            outlined
            height="100%"
            :class="{ 'pb-0 pl-0': editOrAddProductMode }"
          >
            <v-card-title>
              {{ $t('createBooking.productDetails') }}
            </v-card-title>

            <v-card-text class="pb-0 px-2">
              <!-- attribute dropdown list -->
              <v-row class="no-gutters">
                <!-- no attribute keys -->
                <v-col v-if="!attributeKeys.length" class="mt-2">
                  <span class="fal fa-hand-point-left mr-2 fa-2x" />
                  {{ $t('createBooking.selectProduct') }}
                </v-col>

                <!-- iterate attribute keys -->
                <v-col
                  v-for="(attributeKey, attributeKeyIndex) in attributeKeys"
                  :key="attributeKeyIndex"
                  class="col-2 px-1"
                >
                  <v-select
                    outlined
                    :items="getAttributeValues(attributeKey.key)"
                    :label="$t('attributes.' + attributeKey.key)"
                    :item-text="(v) => $t(v.translation)"
                    :value="dropdownStation[attributeKey.key]"
                    return-object
                    @input="
                      (attributeObject) => {
                        changeAttributeValue(attributeKey.key, attributeObject)
                      }
                    "
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- person selection -->
        <v-col
          v-if="displayMode !== 'addProduct' && displayMode !== 'editProduct'"
          class="col-5"
        >
          <v-card outlined height="100%">
            <v-card-title>
              <span v-if="disableUserSelection">
                <span class="fal fa-check green--text mr-2" />
                {{ $t('bookingConfirmation.bookingUser') }}
                {{ selectedUserBookings.getFullName() }}
              </span>
              <span v-else>
                {{ $t('createBooking.selectPerson') }}
              </span>
            </v-card-title>

            <v-card-text>
              <!-- show selected user -->
              <v-row v-if="disableUserSelection" class="no-gutters">
                <v-col v-if="!isSelectedUserMe" class="col-12">
                  <span class="fal fa-hand-point-right mr-2 primary--text" />
                  {{ $t('createBooking.hint') }}
                </v-col>
                <v-col class="col-12 pt-4">
                  <span class="fal fa-info-circle mr-2 primary--text" />
                  {{ $t('createBooking.changeBooking') }}
                </v-col>
              </v-row>

              <!-- search for existing user -->
              <v-row v-else class="no-gutters">
                <v-col class="col-12">
                  <BookingByCustomerSearch
                    ref="bookingByCustomerSearch"
                    booking-mode="hidden"
                    captureUser
                    selectedUserBookingsRequired
                    :selected-user-bookings="selectedUserBookings"
                    @update:selectedUserBookings="
                      (userBookings) => {
                        this.$emit('update:selectedUserBookings', userBookings)
                      }
                    "
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- capacities -->
        <v-col
          v-if="
            productCapacityInstance &&
            productCapacityInstance.hasCapacityOfAnyType()
          "
          :class="[editOrAddProductMode ? 'col-4' : 'col-3']"
        >
          <v-card
            outlined
            height="100%"
            :class="{ 'pt-0': editOrAddProductMode }"
          >
            <v-card-title>
              {{ $t('createBooking.availableCapacity') }}
            </v-card-title>

            <v-card-text :class="{ 'pb-0': editOrAddProductMode }">
              <v-row class="max-height-200 scrollable-y">
                <!-- capacity restrictions -->
                <v-col>
                  <!-- with capacity restrictions -->
                  <div>
                    <!-- iterate capacities -->
                    <div
                      v-for="(valueCapacity, capacityIndex) in valueCapacities"
                      :key="capacityIndex"
                      class="my-2"
                    >
                      <capacity-progress-bar
                        :product="selectedProductInstance"
                        :capacity="valueCapacity"
                        :start-date="selectedDate"
                        :end-date="selectedDate"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- save edited basket entry -->
        <v-col
          v-if="displayMode === 'editProduct'"
          class="text-right align-self-end"
          :class="[
            productCapacityInstance &&
            productCapacityInstance.hasCapacityOfAnyType()
              ? 'col-8'
              : 'col-12',
          ]"
        >
          <v-btn
            outlined
            color="red"
            large
            class="mr-2"
            @click="$emit('cancel')"
          >
            <span class="fal fa-times mr-2" />
            {{ $t('general.cancel') }}
          </v-btn>
          <v-btn outlined color="green" large @click="addToBasket()">
            <span class="fal fa-check mr-2" />
            {{ $t('createBooking.saveChanges') }}
          </v-btn>
        </v-col>

        <!-- add to basket -->
        <v-col
          v-else
          :class="[
            editOrAddProductMode ? 'col-8' : 'col-4',
            { 'pl-0': editOrAddProductMode && hasCapacityOfAnyType },
          ]"
        >
          <v-card
            outlined
            height="100%"
            :class="{ 'py-0': editOrAddProductMode }"
          >
            <v-card-title
              :class="{ 'pl-0': editOrAddProductMode && hasCapacityOfAnyType }"
            >
              {{ $t('createBooking.addToBasket') }}
            </v-card-title>

            <v-card-text :class="{ 'pb-0': editOrAddProductMode }">
              <v-row class="no-gutters">
                <!-- select the amount of products to add into the basket -->
                <v-col class="col-auto">
                  <v-text-field
                    v-model="addProductsAmount"
                    outlined
                    type="number"
                    :label="$t('createBooking.amountOfProducts')"
                    :rules="[
                      (v) =>
                        (!!v && v > 0) || $t('createBooking.amountMandatory'),
                    ]"
                  />
                </v-col>

                <!-- cancel button -->
                <v-col class="mt-2 col-auto text-right">
                  <v-btn
                    v-if="editOrAddProductMode"
                    outlined
                    color="red"
                    large
                    class="ml-2"
                    @click="$emit('cancel')"
                  >
                    <span class="fal fa-times mr-2" />
                    {{ $t('general.cancel') }}
                  </v-btn>

                  <v-btn
                    class="ml-2"
                    outlined
                    color="green"
                    large
                    @click="addToBasket()"
                  >
                    <span class="fal fa-check mr-2" />
                    {{ $t('hotelRoomSelector.addToBasket') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- basket -->
        <v-col
          v-if="displayMode !== 'addProduct' && displayMode !== 'editProduct'"
          class="col-12"
        >
          <v-card outlined>
            <v-card-title>
              {{ $t('v5.basket') }}
            </v-card-title>

            <!-- basket entries -->
            <v-card-text>
              <admin-basket
                :basket-instance="basketInstance"
                :selectedUserBookings="selectedUserBookings"
                :is-selected-user-me="isSelectedUserMe"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <!-- not enough capacities. ask user what to do -->
    <s-modal
      ref="capacityModal"
      :size="2"
      :ok-text="$t('productCapacity.editCapacity')"
      :cancel-text="$t('createBooking.waitingList')"
      @ok="
        $router.push({
          name: 'product',
          params: { id: selectedProductInstance.getId() },
        })
      "
      @cancel="changeBookingState()"
    >
      <v-row>
        <v-col class="col-12">
          <span class="fal fa-exclamation-triangle mr-2 red--text" />
          {{ $t('createBooking.insufficientCapacities') }}
        </v-col>
      </v-row>
    </s-modal>
  </div>
</template>

<script>
import Basket from '../../../classes-shared/basket/Basket'
import SDatePicker from '../../../components-shared/utils/SDatePicker'
import moment from 'moment'
import Products from '../../../classes-shared/products/Products'
import Event from '../../../classes-shared/events/Event'
import Product from '../../../classes-shared/products/Product'
import CapacityProgressBar from '../../products/CapacityProgressBar'
import BookingByCustomerSearch from '../BookingByCustomerSearch'
import UserData from '../../../classes-shared/basket/UserData'
import definitions from '../../../../definitions'
import AdminBasket from './AdminBasket.vue'
import BookingStates from '../../../classes-shared/bookings/BookingStates'
import Attributes from '../../../classes-shared/products/Attributes'
import UserBookings from '../../../classes-shared/bookings/UserBookings'
import ProductSelector from '../../../components-shared/products/ProductSelector'
import _ from 'lodash'

export default {
  name: 'CreateBooking',
  components: {
    ProductSelector,
    AdminBasket,
    BookingByCustomerSearch,
    CapacityProgressBar,
    SDatePicker,
  },
  props: {
    basketInstance: {
      required: true,
      type: Basket,
    },

    bookingStates: {
      type: BookingStates,
      required: true,
    },

    // the selected user with its bookings and shadow users
    selectedUserBookings: {
      type: UserBookings,
      required: true,
    },

    // there are three distinct display modes for the component.
    // 1.) backOffice: Booking for the back office (includes a booking confirmation step)
    // 2.) cashDesk: Booking for the cash desk (includes a printing step)
    // 3.) addProduct: Edit booking (used in BookingDetail.vue => AdminBasket.vue) and add new product
    // 4.) editProduct: Edit product in an existing booking
    displayMode: {
      required: true,
      type: String,
    },
    // to subtract booking entries from capacity count
    bookingInstance: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      // product definition which will be added to the basket next
      selectedProductOrEventInstance: null,

      // format: YYYY-MM-DD
      selectedDate: null,

      // minimal date
      minDate: null,

      // product definition which should be added to the basket
      selectedProductDefinition: null,

      // all products (including event templates)
      productsInstance: new Products(),

      // instanceof helper
      Event: Event,

      // performance helper
      readyToCompute: false,

      // how many products should be added to the basket
      addProductsAmount: 1,
      definitions: definitions,

      // where the values of the dynamic prod def dropdown lives
      dropdownStation: {},
      // basket entry for pre-selection
      basketEntry: null,
      readyForPreselection: false,
      preventLoadingEvents: false,
    }
  },

  computed: {
    // mixing products and events into one array for the drop down
    productsAndEvents() {
      if (this.readyToCompute && this.productsInstance.products.length) {
        let productsAndEvents = []
        // get products only (exclude event template products)
        productsAndEvents = this.productsInstance.getProducts(true)

        let eventProducts = this.productsInstance.getEventProducts()
        for (let i = 0; i < eventProducts.length; i++) {
          let events = eventProducts[i].events.events

          for (let event in events) {
            productsAndEvents.push(events[event])
          }
        }

        return productsAndEvents
      }
      return []
    },

    // this is the product instance which is selected, whether an event or a product is selected in the dropdown
    selectedProductInstance() {
      if (this.selectedProductOrEventInstance instanceof Event)
        return this.productsInstance.getProductInstanceByEventId(
          this.selectedProductOrEventInstance.id
        )
      else if (this.selectedProductOrEventInstance instanceof Product)
        return this.selectedProductOrEventInstance
      else return null
    },

    // short cut
    productCapacityInstance() {
      if (this.selectedProductInstance) {
        return this.selectedProductInstance.getProductCapacity()
      }
      return null
    },

    valueCapacities() {
      if (this.productCapacityInstance) {
        let date = new Date(this.selectedDate)
        date.setHours(0, 0, 0, 0)
        return this.productCapacityInstance.getCapacitiesbyDate(date)
      }
      return []
    },

    // all possible attribute keys depending on the selected product instance
    attributeKeys() {
      if (this.selectedProductInstance)
        return this.selectedProductInstance.getAllAttributeNames(true)
      return []
    },

    // helper
    disableUserSelection() {
      return (
        this.selectedUserBookings &&
        this.basketInstance.getBasketEntries().length > 0
      )
    },

    // date helpers
    allowedDates() {
      if (this.selectedProductInstance)
        return this.selectedProductInstance.getAvailableDates('dateString')
      return []
    },

    maxDate() {
      if (this.selectedProductInstance)
        return moment(
          this.selectedProductInstance.getCurrentSeasonEnd()
        ).format('YYYY-MM-DD')
      return undefined
    },

    isSelectedUserMe() {
      if (this.selectedUserBookings) {
        return (
          this.selectedUserBookings.getId() ===
          this.$store.getters.getAppUserInstance().getId()
        )
      }
      return false
    },
    // short cut
    editOrAddProductMode() {
      return (
        this.displayMode === 'editProduct' || this.displayMode === 'addProduct'
      )
    },
    // short cut
    hasCapacityOfAnyType() {
      return (
        this.productCapacityInstance &&
        this.productCapacityInstance.hasCapacityOfAnyType()
      )
    },
  },

  watch: {
    selectedDate() {
      if (!this.preventLoadingEvents) {
        this.loadEvents()
      }
      this.loadCapacities()
    },

    // init point, if a product was selected
    selectedProductInstance() {
      this.setStandardProductDefinition()
      this.initDropdownStation()
      // initializes min date and sets selected date
      this.initMinDate()
      this.loadCapacities()
    },
  },

  async mounted() {
    // init products and events
    this.initProductsAndEvents()
  },

  methods: {
    // set the min date, if product selection was changed was changed
    async initMinDate() {
      if (this.selectedProductInstance) {
        let nextDateFromToday = await this.selectedProductInstance.getNextPossibleBookingDate(
          null,
          null,
          true,
          true,
          true,
          null,
          false
        )
        if (!nextDateFromToday) return null
        this.minDate = nextDateFromToday.format('YYYY-MM-DD')
      } else {
        this.minDate = null
      }
      // set selected date, except it's an event
      if (!(this.selectedProductOrEventInstance instanceof Event)) {
        // it's not an event
        if (this.selectedDate) {
          // already a date selected, try to take over
          let tmpSelectedDate = this.selectedDate
          let newSelectedDate = await this.selectedProductOrEventInstance.getNextPossibleBookingDate(
            moment(this.selectedDate)
              .hours(0)
              .minutes(0)
              .seconds(0)
              .milliseconds(0),
            null,
            true,
            true,
            true,
            null,
            false
          )
          if (newSelectedDate)
            this.selectedDate = newSelectedDate.format('YYYY-MM-DD')
          // found the next date
          else this.selectedDate = null // out of season
          // notify user, whenever date has changed
          if (tmpSelectedDate !== this.selectedDate)
            EventBus.$emit(
              'notify',
              this.$t('createBooking.dateNotAvailableForSelectedProduct')
            )
        } else {
          // set for the first time a date
          this.selectedDate = this.minDate
        }
      }
      return true
    },

    // add definitions to basket
    async addToBasket() {
      if (!this.selectedProductDefinition) {
        EventBus.$emit(
          'notify',
          this.$t('createBooking.noSuchProductCombination')
        )
        return
      }

      // check form
      if (!this.$refs.bookingForm.validate()) return

      // spinner
      EventBus.$emit('spinnerShow')

      // short cut
      let dateInstance = new Date(this.selectedDate)
      dateInstance.setHours(0, 0, 0, 0)

      // check capacity
      if (!this.bookingStates.isWaitingList() && this.productCapacityInstance) {
        // calc stock left
        let attributeKey = this.productCapacityInstance.getKey()
        let attributeValue = null
        if (attributeKey) {
          // get attribute value for the selected product definition
          attributeValue = this.selectedProductDefinition.getAttributes()[
            attributeKey
          ][definitions.attributeValues.value]
        }

        let clonedBasket = null
        if (this.bookingInstance) {
          let clonedBooking = _.cloneDeep(this.bookingInstance)
          clonedBasket = _.cloneDeep(this.basketInstance)
          clonedBasket.removeBasketEntriesContainedInBooking(clonedBooking)
        }
        let stockLeft = this.productCapacityInstance.getStockLeft(
          dateInstance,
          attributeValue,
          clonedBasket || this.basketInstance
        )
        // if we're in editProduct mode, add the capacity of the current product definition
        if (this.displayMode === 'editProduct') {
          stockLeft += this.selectedProductDefinition.getCapacityCount()
        }

        // insufficient stock
        if (
          Number.isFinite(stockLeft) &&
          stockLeft <
            this.addProductsAmount *
              this.selectedProductDefinition.getCapacityCount()
        ) {
          EventBus.$emit('spinnerHide')
          // open modal
          if (this.displayMode === 'backOffice') this.$refs.capacityModal.show()
          else EventBus.$emit('notify', this.$t('createBooking.productSoldOut'))
          return
        }
      }

      let user = this.selectedUserBookings
      if (!user) throw new Error('UID not provided for basket!')

      let isEvent = this.selectedProductOrEventInstance instanceof Event
      let userData = new UserData({
        media: definitions.ticketMedia.paper,
        uid: user.getUid(),
        user: {
          uid: user.getUid(),
          firstName: user.getFirstName(),
          lastName: user.getLastName(),
          mail: user.getMail(),
          birthdate: user.getBirthdate(),
        },
        eventId: isEvent ? this.selectedProductOrEventInstance.id : null,
        eventName: isEvent ? this.selectedProductOrEventInstance.title : null,
      })
      // if we want to add an event, we need to check the data is only on the event date
      if (isEvent) {
        let eventDate = new Date(this.selectedProductOrEventInstance.date)
        if (eventDate.setHours(0, 0, 0, 0) !== dateInstance.getTime()) {
          EventBus.$emit(
            'notify',
            this.$t('createBooking.eventNotAvailableOnThisDate')
          )
          EventBus.$emit('spinnerHide')
          return
        }
      }

      // edit basket
      if (this.basketEntry) {
        await this.basketInstance.deleteBasketEntry(this.basketEntry.getId())
        await this.basketInstance.addProductDefinitionsToBasket(
          1,
          this.selectedProductDefinition,
          dateInstance,
          userData
        )
      } else {
        // add to basket
        await this.basketInstance.addProductDefinitionsToBasket(
          this.addProductsAmount,
          this.selectedProductDefinition,
          dateInstance,
          userData,
          true
        )
      }
      this.$emit('basketEntryAddedOrEdited')
      EventBus.$emit('spinnerHide')
    },

    changeBookingState() {
      this.bookingStates.setBookingState(definitions.bookingState.waiting_list)
      this.$nextTick(() => {
        this.addToBasket()
      })
    },

    // init function called from mounted hook
    async initProductsAndEvents() {
      // performance
      this.readyToCompute = false
      // load products
      await this.productsInstance.loadProducts()
      this.readyToCompute = true

      // if a date was passed by route, set it as selected date, also to load events
      if (this.$route.params.date) {
        this.selectedDate = moment(this.$route.params.date).format('YYYY-MM-DD')
      }
      // if a date was passed by props, set it as selected date, also to load events
      this.readyForPreselection = true
      if (this.basketEntry) {
        this.setBasketEntryForPreselection(this.basketEntry)
      }
    },

    // short cut
    async loadEvents() {
      if (!this.selectedDate) return
      return await this.productsInstance.fetchEventsFromApi(
        this.productsInstance.getProducts(),
        false,
        true,
        'compleat',
        moment(this.selectedDate),
        moment(this.selectedDate)
      )
    },

    // short cut
    async loadCapacities() {
      if (this.selectedProductInstance && this.selectedDate) {
        await this.selectedProductInstance.fetchCapacities(
          moment(this.selectedDate),
          moment(this.selectedDate)
        )
      }
      return true
    },

    // dropdown helper
    getAttributeValues(attributeKey) {
      if (this.selectedProductInstance) {
        const attributeValues = this.selectedProductInstance.getPossibleAttributeValuesByKey(
          attributeKey
        )
        return attributeValues.map(
          (attributeValue) => attributeValue[attributeKey]
        )
      }
      return []
    },

    // dropdown helper
    changeAttributeValue(attributeKey, attributeObject) {
      // set new station
      this.dropdownStation[attributeKey] = attributeObject

      // search for a new product definition
      let attributePairs = Attributes.parseAttributeObjectToAttributePairs(
        this.dropdownStation
      )
      let newProductDefinition = this.selectedProductInstance.filterProductDefinitionsByAttributes(
        attributePairs,
        true,
        true
      )

      // set new selected product definition
      if (newProductDefinition)
        this.selectedProductDefinition = newProductDefinition
      else {
        this.selectedProductDefinition = null
        EventBus.$emit(
          'notify',
          this.$t('createBooking.noSuchProductCombination'),
          'error',
          { noModal: true }
        )
      }
    },
    // if product instance changes, set the defined standard product definition
    setStandardProductDefinition() {
      if (!this.selectedProductInstance) {
        // reset selected products
        this.selectedProductOrEventInstance = null
        this.selectedProductDefinition = null
      } else {
        // only change selected product definition, if passed basket entry's product definition id is not of
        // the current product
        if (
          !this.basketEntry ||
          !this.selectedProductInstance.getProductDefinitionInstance(
            this.basketEntry.getProductDefinitionId()
          )
        ) {
          this.selectedProductDefinition = this.selectedProductInstance.getStandardProductDefinition()
        }
      }
    },
    initDropdownStation() {
      if (this.selectedProductDefinition) {
        let attributes = this.selectedProductDefinition.getAttributes()
        // reset dropdown station
        this.dropdownStation = {}

        for (let attribute in attributes) {
          this.dropdownStation[attribute] = attributes[attribute]
        }
      }
    },
    // pre-select product definition and date
    async preselectDateAndProductDefinition(
      productDefinitionId,
      dateString,
      eventId
    ) {
      // handling events
      this.preventLoadingEvents = true
      this.selectedDate = dateString
      await this.loadEvents()
      this.preventLoadingEvents = false
      // pre-select product or event
      const productInstance = this.productsInstance.getProductInstanceByProductDefinitionId(
        productDefinitionId
      )
      const eventInstance = productInstance.events.findEventId(eventId)
      this.setSelectedProductOrEventInstance(eventInstance || productInstance)
      // set prod def
      this.selectedProductDefinition = productInstance.getProductDefinitionInstance(
        productDefinitionId
      )
      this.initDropdownStation()
    },
    // method for parent components
    setSelectedProductOrEventInstance(productOrEventInstance) {
      this.selectedProductOrEventInstance = productOrEventInstance
    },
    // method for parent components to pre-select a date
    setDate(dateString) {
      this.selectedDate = dateString
    },
    // method for parent components
    setBasketEntryForPreselection(basketEntry) {
      this.basketEntry = basketEntry
      if (this.readyForPreselection) {
        const productDefinitionId = this.basketEntry.getProductDefinitionId()
        const eventId = this.basketEntry.getEventId()
        this.preselectDateAndProductDefinition(
          productDefinitionId,
          this.basketEntry.getValidFrom('YYYY-MM-DD'),
          eventId
        )
      }
    },
    // reset eventually made pre-selections of date, basket, product or event
    resetPreselection() {
      this.basketEntry = null
      this.selectedDate = null
      this.selectedProductDefinition = null
      this.selectedProductOrEventInstance = null
    },
  },
}
</script>
